import React, { useCallback, useState, forwardRef, useImperativeHandle } from 'react';
import classNames from 'classnames';
import { utc } from 'moment';

import { formatDate } from '~/app/helpers/date';
import FormattedDate from '~/app/common/FormattedDate';

import useField from '../useField';
import useDefaultValue from '../useDefaultValue';

import Field, { FieldProps } from '../Field';

import mainClasses from '../style.module.scss';
import classes from './style.module.scss';

export interface DateRefObject {
  setValue: React.Dispatch<React.SetStateAction<string>>
}

export interface DatePropsDefault extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  inputClassName?: string
  type?: 'date' | 'time' | 'datetime-local'
  format?: string
  strongTime?: boolean
}

export type DateProps = DatePropsDefault & FieldProps

export type DateComponent = React.ForwardRefRenderFunction<DateRefObject, DateProps>

const Date: DateComponent = (props, ref) => {
  const {
    fieldProps,
    inputProps: {
      inputClassName,
      type = 'datetime-local',
      placeholder,
      format,
      defaultValue: defaultDefaultValue,
      strongTime,
      onChange,
      ...othersInputProps
    }
  } = useField<DatePropsDefault>(props);

  const defaultValue = useDefaultValue(defaultDefaultValue);

  const getFormattedDate = useCallback((date) => {
    let format = 'YYYY-MM-DD[T]HH:mm';
    if (type === 'date') {
      format = 'YYYY-MM-DD';
    }

    return formatDate(date, format)
  }, [type]);

  const [value, setValue] = useState<string>(defaultValue ? getFormattedDate(defaultValue) : '');

  const inputChangeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    let newValue = getFormattedDate(e.target.value);
    
    if (othersInputProps.min && utc(e.target.value).isBefore(othersInputProps.min)) {
      newValue = getFormattedDate(othersInputProps.min);
    } else if (othersInputProps.max && utc(e.target.value).isAfter(othersInputProps.max)) {
      newValue = getFormattedDate(othersInputProps.max);
    }

    setValue(newValue);

    if (onChange) {
      onChange({ ...e, target: { ...e.target, name: e.target.name, value: newValue } });
    }

  }, [onChange, othersInputProps.max, othersInputProps.min, getFormattedDate]);

  useImperativeHandle(ref, () => ({
    setValue
  }), []);

  return (
    <Field {...fieldProps}>
      <div className={classNames(`${mainClasses['input']} ${classes['input']}`, inputClassName)}>

        <FormattedDate strongTime={strongTime} date={value} format={format} placeholder={placeholder} />

        <input
          {...othersInputProps}
          className={classes['date-input']}
          type={type}
          value={value}
          disabled={fieldProps.loading || othersInputProps.disabled}
          onChange={inputChangeHandler}
        />
      </div>
    </Field>
  );
};

export default forwardRef(Date)
